import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { PageLayout, MainContent, ContentContainer } from '../../styles/SharedStyles';
import ExpertSidebar from '../../components/expert/ExpertSidebar';
import {
  Box,
  Typography,
  Checkbox,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  useMediaQuery,
  useTheme,
  Card,
  CardContent,
  Chip,
  Divider,
  Tabs,
  Tab,
  Grid,
  TextField,
  Toolbar,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { TimePicker } from '@mui/x-date-pickers/TimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { useNavigate } from 'react-router-dom';
import { PageTitle } from '../../styles/SharedStyles';
import {
  SwapHoriz as SwapHorizIcon,
  Archive as ArchiveIcon,
  Group as GroupIcon,
  Person as PersonIcon,
  AccessTime as AccessTimeIcon,
  CalendarToday as CalendarTodayIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  CheckCircle as CheckCircleIcon,
  Download as DownloadIcon,
  CalendarTodayOutlined as CalendarTodayOutlinedIcon,
  ContentCopy as ContentCopyIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import { useLeads } from '../../redux/slices/leads/useLeads';
import { Lead, LeadExt } from '../../types/leads';
import { useMeetings } from '../../redux/slices/meetings/useMeetings';
import { useExpert } from '../../redux/slices/expert/useExpert';
import { useBusiness } from '../../redux/slices/business/useBusiness';
import moment from 'moment';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const TabPanel = (props: TabPanelProps) => {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`leads-tabpanel-${index}`}
      aria-labelledby={`leads-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ py: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
};

const LeadsPage: React.FC = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isAdminUser = true; // FIXME: Need to be considered after confirming DB schema

  const { body: leads } = useLeads()
  const { body: meetings } = useMeetings()
  const { body: expert } = useExpert()
  const { body: business } = useBusiness()

  const [extLeads, setExtLeads] = useState<LeadExt[]>([]);
  const [selectedLeads, setSelectedLeads] = useState<string[]>([]);
  const [selectAll, setSelectAll] = useState(false);
  const [schedulingLead, setSchedulingLead] = useState<Lead | null>(null);
  const [schedulingDate, setSchedulingDate] = useState<Date | null>(null);
  const [schedulingTime, setSchedulingTime] = useState<Date | null>(null);
  const [tabValue, setTabValue] = useState(0);
  const [showDetails, setShowDetails] = useState(false);
  const [selectedDetails, setSelectedDetails] = useState<LeadExt | null>(null);

  useEffect(() => {
    const extLeads = leads.map((l) => {
      const meeting_scheduled = !!meetings.find(m => m.expert_id = l.expert_id)
      return { ...l, meeting_scheduled: meeting_scheduled, business_id: business?.id, owner: '' }
    })

    setExtLeads(extLeads);
  }, [leads, meetings])

  const getTeamMemberColor = (name: string) => {
    const colors = [
      '#FF6B6B', // Coral Red
      '#4ECDC4', // Turquoise
      '#45B7D1', // Sky Blue
      '#96CEB4', // Sage Green
      '#FFEEAD', // Soft Yellow
      '#D4A5A5', // Dusty Rose
      '#9B5DE5', // Purple
      '#00BBF9', // Bright Blue
      '#00F5D4', // Mint
      '#FEE440'  // Yellow
    ];

    const charSum = name.split('').reduce((sum, char) => sum + char.charCodeAt(0), 0);
    return colors[charSum % colors.length];
  };

  const filterLeads = (leads: LeadExt[]) => {
    switch (tabValue) {
      case 0: // My Leads
        return leads.filter(lead => lead.expert_id === expert?.id && lead.status !== 'archived');
      case 1: // Team Leads
        return leads.filter(lead => lead.business_id === business?.id && lead.status !== 'archived');
      case 2: // Archived
        return leads.filter(lead => lead.status === 'archived');
      default:
        return leads;
    }
  };

  const filteredLeads = filterLeads(extLeads);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
    setSelectedLeads([]);
    setSelectAll(false);
  };

  const handleSelectAll = () => {
    if (selectAll) {
      setSelectedLeads([]);
    } else {
      setSelectedLeads(filteredLeads.map(lead => lead.id));
    }
    setSelectAll(!selectAll);
  };

  const formatParticipant = (details?: {
    numAdults?: number;
    numChildren?: number;
    numInfants?: number;
  }) => {
    if (!details) return '';

    const { numAdults = 0, numChildren = 0, numInfants = 0 } = details;

    const parts: string[] = [];

    if (numAdults > 0) {
      parts.push(`${numAdults} Adult${numAdults > 1 ? 's' : ''}`);
    }
    if (numChildren > 0) {
      parts.push(`${numChildren} Child${numChildren > 1 ? 'ren' : ''} (2 - 12yo)`);
    }
    if (numInfants > 0) {
      parts.push(`${numInfants} Infant${numInfants > 1 ? 's' : ''}`);
    }

    return parts.join(', ');
  };


  const handleSelectLead = (id: string) => {
    if (selectedLeads.includes(id)) {
      setSelectedLeads(selectedLeads.filter(leadId => leadId !== id));
      setSelectAll(false);
    } else {
      setSelectedLeads([...selectedLeads, id]);
      if (selectedLeads.length + 1 === filteredLeads.length) {
        setSelectAll(true);
      }
    }
  };

  const handleConfirmSchedule = () => {
    if (schedulingLead && schedulingDate && schedulingTime) {
      console.log('New meeting scheduled:', {
        lead: schedulingLead,
        date: schedulingDate,
        time: schedulingTime
      });
      setSchedulingLead(null);
      setSchedulingDate(null);
      setSchedulingTime(null);
      navigate('/expert/meet');
    }
  };

  const handleReassignLead = (lead: Lead) => {
    console.log('Reassign lead:', lead);
  };

  const renderLeadCard = (lead: LeadExt) => {
    const isMyLead = lead.expert_id === expert?.id;

    if (!lead.trip_details) return

    const startDate = moment(lead.trip_details.startDate);
    const endDate = moment(lead.trip_details.endDate);
    const duration = endDate.diff(startDate, "d")

    const timestamp = moment(lead.created_at).format("MMMM D YYYY")

    return (
      <LeadCard key={lead.id} elevation={0}>
        <CompactCardContent>
          <Box display="flex" alignItems="flex-start">
            <Checkbox
              checked={selectedLeads.includes(lead.id)}
              onChange={() => handleSelectLead(lead.id)}
              size="small"
            />
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', md: '2fr 2fr 2fr 4fr' },
                gap: 2,
                mt: 1,
                opacity: !isMyLead && !isAdminUser ? 0.7 : 1,
                width: "100%",
              }}
              onClick={() => {
                setSelectedDetails(lead)
                setShowDetails(true)
              }}
            >

              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, minWidth: 0 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0, flex: 1 }}>
                    <Typography variant="subtitle1" noWrap>
                      {lead.name}
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
                      {lead.status == "completed" && (
                        <Tooltip title="Completed">
                          <CheckCircleIcon color="success" sx={{ fontSize: 18 }} />
                        </Tooltip>
                      )}
                      {lead.meeting_scheduled && (
                        <Tooltip title="Meeting Scheduled">
                          <CalendarTodayIcon color="primary" sx={{ fontSize: 18 }} />
                        </Tooltip>
                      )}
                      <Tooltip title={lead.expert_id === expert?.id ? "My Lead" : `Owned by ${lead.expert_id}`}>
                        <Box component="span">
                          {lead.expert_id === expert?.id ? (
                            <PersonIcon color="primary" sx={{ fontSize: 18 }} />
                          ) : (
                            <Box sx={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 0.5,
                              backgroundColor: getTeamMemberColor(lead.expert_id),
                              color: '#fff',
                              padding: '2px 8px',
                              borderRadius: '12px',
                              fontSize: '0.75rem',
                              opacity: isAdminUser ? 1 : 0.7,
                              cursor: isAdminUser ? 'pointer' : 'not-allowed'
                            }}>
                              <GroupIcon sx={{ fontSize: 14 }} />
                              {lead.expert_id}
                              {isAdminUser && (
                                <Tooltip title="Reassign Lead">
                                  <IconButton
                                    size="small"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleReassignLead(lead);
                                    }}
                                    sx={{
                                      ml: 0.5,
                                      color: 'inherit',
                                      '&:hover': {
                                        backgroundColor: 'rgba(255, 255, 255, 0.2)'
                                      }
                                    }}
                                  >
                                    <SwapHorizIcon sx={{ fontSize: 14 }} />
                                  </IconButton>
                                </Tooltip>
                              )}
                            </Box>
                          )}
                        </Box>
                      </Tooltip>
                    </Box>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <EmailIcon fontSize="small" color="action" sx={{ fontSize: 16 }} />
                    <Typography variant="body2" noWrap>{lead.email}</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <PhoneIcon fontSize="small" color="action" sx={{ fontSize: 16 }} />
                    <Typography variant="body2" noWrap>{lead.phone}</Typography>
                  </Box>
                </Box>
              </Box>

              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, minWidth: 0, borderRadius: 2, background: '#f5f5f5', padding: 1 }}>
                <Typography variant="body2" color="text.secondary" noWrap>
                  {lead.trip_details?.tripName}
                </Typography>
                <Typography variant="body2" color="text.secondary" noWrap>
                  {lead.trip_details.budgetCurrency}{lead.trip_details?.minBudget}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, minWidth: 0, borderRadius: 2, background: '#f5f5f5', padding: 1 }}>
                <Typography variant="body2" color="text.secondary">
                  {lead.trip_details?.startDate} <br /> to {lead.trip_details?.endDate}
                </Typography>
                <Typography variant="body2" color="text.secondary" noWrap>
                  {duration} Days
                </Typography>
              </Box>

              <Box sx={{ display: 'flex', justifyContent: 'flex-end', minWidth: "160px" }}>
                <Box display="flex" flexDirection="column" alignItems="end" gap={1}>
                  {lead.meeting_scheduled && <Box display="flex" alignItems="center" gap={1}>
                    <CalendarTodayIcon color="error" />
                    <Typography variant='body2'>3.45am (GMT + 10:00) Wed, 12th Dec 2024</Typography>
                  </Box>}
                  <Button
                    variant="outlined"
                    size="small"
                    color={lead.status == "completed" && 'warning' || lead.meeting_scheduled && "secondary" || 'primary'}
                    startIcon={<CalendarTodayIcon sx={{ fontSize: 16 }} />}
                    onClick={() => navigate(`/${expert?.preferred_link}`, { state: { directCalendar: true } })}
                    sx={{ minWidth: 'auto', px: 1 }}
                    disabled={!isMyLead && !isAdminUser}
                  >
                    {lead.status == "completed" && '+ Meeting' || lead.meeting_scheduled && 'Re-schedule' || 'Schedule'}
                  </Button>
                </Box>
              </Box>
            </Box>
          </Box>
          <Box>
            <Typography fontSize={10} paddingX={5}>{timestamp}</Typography>
          </Box>
        </CompactCardContent>
      </LeadCard>
    );
  };

  return (
    <PageLayout>
      <ExpertSidebar />
      <MainContent>
        <ContentContainer>
          <PageTitle variant="h4" fontWeight={600} gutterBottom>Leads</PageTitle>
          <Box sx={{
            display: 'flex',
            flexDirection: isMobile ? 'column-reverse' : 'row',
            justifyContent: 'space-between',
            alignItems: isMobile ? 'flex-start' : 'center',
            flexWrap: 'wrap',
            gap: 2,
            mb: 3,
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
              <Chip label="100 Visitors" />
              <Chip label="20 Leads" />
              <Chip label="15 Meetings" />
            </Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 2, alignSelf: "self-end" }}>
              <Button
                variant="outlined"
                size={isMobile ? 'small' : 'medium'}
              >
                <DownloadIcon />
              </Button>
              <Button
                variant="outlined"
                size={isMobile ? 'small' : 'medium'}
                sx={{
                  display: "flex",
                  gap: 1,
                }}
                onClick={() => navigate('/expert/meet')}
              >
                <CalendarTodayOutlinedIcon />
              </Button>
            </Box>
          </Box>

          <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
            <Tabs
              value={tabValue}
              onChange={handleTabChange}
              aria-label="leads tabs"
              variant={isMobile ? "fullWidth" : "standard"}
            >
              <Tab
                icon={<PersonIcon />}
                iconPosition="start"
                label="My Leads"
                sx={{ minHeight: 48 }}
              />
              <Tab
                icon={<GroupIcon />}
                iconPosition="start"
                label="Team Leads"
                sx={{ minHeight: 48 }}
              />
              <Tab
                icon={<ArchiveIcon />}
                iconPosition="start"
                label="Archived"
                sx={{ minHeight: 48 }}
              />
            </Tabs>
          </Box>

          <TabPanel value={tabValue} index={0}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <Checkbox
                checked={selectAll}
                onChange={handleSelectAll}
                sx={{ mr: 1 }}
              />
              <Typography variant="body2" color="text.secondary">
                Select All
              </Typography>
            </Box>
            <LeadsList>
              {filteredLeads.map(lead => renderLeadCard(lead))}
            </LeadsList>
          </TabPanel>

          <TabPanel value={tabValue} index={1}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <Checkbox
                checked={selectAll}
                onChange={handleSelectAll}
                sx={{ mr: 1 }}
              />
              <Typography variant="body2" color="text.secondary">
                Select All
              </Typography>
            </Box>
            <LeadsList>
              {filteredLeads.map(lead => renderLeadCard(lead))}
            </LeadsList>
          </TabPanel>

          <TabPanel value={tabValue} index={2}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <Checkbox
                checked={selectAll}
                onChange={handleSelectAll}
                sx={{ mr: 1 }}
              />
              <Typography variant="body2" color="text.secondary">
                Select All
              </Typography>
            </Box>
            <LeadsList>
              {filteredLeads.map(lead => renderLeadCard(lead))}
            </LeadsList>
          </TabPanel>

          <Dialog
            open={!!schedulingLead}
            onClose={() => setSchedulingLead(null)}
            fullScreen={isMobile}
            maxWidth="sm"
            fullWidth
          >
            <DialogTitle>
              Schedule Meeting with {schedulingLead?.name}
            </DialogTitle>
            <DialogContent>
              <Box sx={{ mt: 2 }}>
                <LocalizationProvider dateAdapter={AdapterDateFns}>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                    <DatePicker
                      label="Date"
                      value={schedulingDate}
                      onChange={(newDate) => setSchedulingDate(newDate)}
                    />
                    <TimePicker
                      label="Time"
                      value={schedulingTime}
                      onChange={(newTime) => setSchedulingTime(newTime)}
                    />
                  </Box>
                </LocalizationProvider>
              </Box>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setSchedulingLead(null)}>Cancel</Button>
              <Button
                onClick={handleConfirmSchedule}
                variant="contained"
                disabled={!schedulingDate || !schedulingTime}
              >
                Schedule
              </Button>
            </DialogActions>
          </Dialog>
        </ContentContainer>
      </MainContent>
      <Dialog open={showDetails} onClose={() => setShowDetails(false)} maxWidth="lg" fullScreen={isMobile}>
        <DialogTitle>
          <Typography variant='h6'>Enquiry Details</Typography>
          <IconButton
            color="inherit"
            onClick={() => setShowDetails(false)}
            aria-label="close"
            sx={{
              position: "absolute",
              top: 8,
              right: 8,
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Divider />
          <Grid container spacing={1} mt={1}>
            <Grid item xs={12} sm={6} md={4}>
              <Grid container spacing={1}>
                {/* Row 1: What */}
                <Grid item xs={3}>
                  <Typography fontWeight="bold">What:</Typography>
                </Grid>
                <Grid item xs={9}>
                  <Typography>
                    {selectedDetails?.trip_details?.tripName}{", "}
                    {selectedDetails?.trip_details?.tripType}{", "}
                    {selectedDetails?.trip_details?.budgetCurrency} {selectedDetails?.trip_details?.minBudget} - {selectedDetails?.trip_details?.maxBudget}
                  </Typography>
                </Grid>

                {/* Row 2: Where */}
                <Grid item xs={3}>
                  <Typography fontWeight="bold">Where:</Typography>
                </Grid>
                <Grid item xs={9}>
                  <Typography>From: {selectedDetails?.trip_details?.originLocation}</Typography>
                  <Typography>To: {selectedDetails?.trip_details?.tripLocation}</Typography>
                  <Typography>Precision: {selectedDetails?.trip_details?.locationPrecision}</Typography>
                </Grid>

                {/* Row 3: When */}
                <Grid item xs={3}>
                  <Typography fontWeight="bold">When:</Typography>
                </Grid>
                <Grid item xs={9}>
                  <Typography>From: {selectedDetails?.trip_details?.startDate}</Typography>
                  <Typography>To: {selectedDetails?.trip_details?.endDate}</Typography>
                  <Typography>Precision: {selectedDetails?.trip_details?.timingPrecision}</Typography>
                </Grid>

                {/* Row 4: Who */}
                <Grid item xs={3}>
                  <Typography fontWeight="bold">Who:</Typography>
                </Grid>
                <Grid item xs={9}>
                  <Typography>{formatParticipant(selectedDetails?.trip_details)}</Typography>
                </Grid>
              </Grid>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <Grid container spacing={1}>
                {/* Row 1: Travel */}
                <Grid item xs={3}>
                  <Typography fontWeight="bold">Travel:</Typography>
                </Grid>
                <Grid item xs={9}>
                  <Typography>{selectedDetails?.trip_details?.transportType}</Typography>
                  <Typography sx={{ fontStyle: 'italic' }}>
                    (Eg. Flights, long rail/bus etc.)
                  </Typography>
                  <Typography>Type - {selectedDetails?.trip_details?.pricingType}</Typography>
                  {selectedDetails?.trip_details?.preferredCarrier && <Typography>Pref. {selectedDetails?.trip_details?.preferredCarrier}</Typography>}
                  <Typography>{selectedDetails?.trip_details?.transportType == 'All' ? 'Include on-demand transfers' : 'Exclude on-demand transfers'}</Typography>
                  <Typography sx={{ fontStyle: 'italic' }}>
                    (Eg. Taxis, short bus/rail etc.)
                  </Typography>
                </Grid>

                {/* Row 2: Accommodation */}
                <Grid item xs={3}>
                  <Typography fontWeight="bold">Accom:</Typography>
                </Grid>
                <Grid item xs={9}>
                  <Typography>Price = {selectedDetails?.trip_details?.accommodationType}</Typography>
                  {selectedDetails?.trip_details?.preferredBrand && <Typography>Pref. {selectedDetails?.trip_details?.preferredBrand}</Typography>}
                </Grid>
              </Grid>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <Grid container spacing={1}>
                {/* Row 1: Meals */}
                <Grid item xs={3}>
                  <Typography fontWeight="bold">Meals:</Typography>
                </Grid>
                <Grid item xs={9}>
                  {!!selectedDetails?.trip_details?.numBreakfastVenue &&
                    <>
                      <Typography>{selectedDetails?.trip_details?.numBreakfastVenue} Breakfast / {selectedDetails?.trip_details?.breakfastVenueType}</Typography>
                      <Typography>Price = {selectedDetails?.trip_details?.breakfastPricingType}</Typography>
                    </>}
                  {!!selectedDetails?.trip_details?.numLunchVenue &&
                    <>
                      <Typography>{selectedDetails?.trip_details?.numLunchVenue} Lunch / {selectedDetails?.trip_details?.lunchVenueType}</Typography>
                      <Typography>Price = {selectedDetails?.trip_details?.lunchPricingType}</Typography>
                    </>}
                  {!!selectedDetails?.trip_details?.numDinnerVenue &&
                    <>
                      <Typography>{selectedDetails?.trip_details?.numDinnerVenue} Dinner / {selectedDetails?.trip_details?.dinnerVenueType}</Typography>
                      <Typography>Price = {selectedDetails?.trip_details?.dinnerPricingType}</Typography>
                    </>}
                </Grid>

                {/* Row 2: Activities */}
                <Grid item xs={3}>
                  <Typography fontWeight="bold">Activ:</Typography>
                </Grid>
                <Grid item xs={9}>
                  <Typography>{selectedDetails?.trip_details?.activities.join(", ")}</Typography>
                </Grid>

                {/* Row 3: Info */}
                <Grid item xs={3}>
                  <Typography fontWeight="bold">Info:</Typography>
                </Grid>
                <Grid item xs={9}>
                  <Typography>
                    {selectedDetails?.trip_details?.comments}
                  </Typography>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
          <TextField
            label="Notes"
            margin="dense"
            fullWidth
            multiline
            rows={4}
          />
          <Box display="flex" justifyContent="flex-end">
            <Box display="flex" gap={1}>
              <Button variant="outlined">
                <ContentCopyIcon />
              </Button>
              <Button variant="outlined">
                <DownloadIcon />
              </Button>
            </Box>
          </Box>
        </DialogContent>
      </Dialog>
    </PageLayout>
  );
};

const LeadsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const LeadCard = styled(Card)`
  width: 100%;
  transition: all 0.2s ease-in-out;
  border: 1px solid #e0e0e0;

  &:hover {
    border-color: #bdbdbd;
    background-color: #fafafa;
  }
`;

const CompactCardContent = styled(CardContent)`
  padding: 12px !important;
  &:last-child {
    padding-bottom: 12px !important;
  }
  &: hover {
    cursor: pointer;
  }
`;

export default LeadsPage;
